import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ValidationErrors } from '@angular/forms';
import { FloatLabelType } from '@angular/material/form-field';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MunilyDatePickerModalComponent } from '../date-picker-modal/date-picker-modal.component';
import {
  MUNILY_DATE_PICKER_MODAL_PANEL_CLASS,
  MunilyDatePickerModalData,
} from '../date-picker-modal/date-picker-modal.types';
import {
  MunilyDateFieldAppearance,
  MunilyDateFieldErrorMessages,
  MunilyDateFieldSize,
  MunilyDateFieldWidthLayout,
} from './date-field.types';

@Component({
  selector: 'app-date-field',
  templateUrl: './date-field.component.html',
  styleUrls: ['./date-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() control?: FormControl;

  @Input() appearance: MunilyDateFieldAppearance = 'outlined';
  @Input() size: MunilyDateFieldSize = 'default';
  @Input() widthLayout: MunilyDateFieldWidthLayout = 'ds';
  @Input() maxWidth?: string;
  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() iconLeading = 'event';
  @Input() floatLabel: FloatLabelType = 'auto';
  @Input() disabled = false;
  @Input() successful = false;
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  /** ISO `yyyy-MM-dd` dates that cannot be selected in the picker */
  @Input() disabledDates: string[] = [];
  @Input() pickerTitleKey?: string;
  @Input() errorMessage = '';
  @Input() errorMessages: MunilyDateFieldErrorMessages = {};

  @Output() valueChange = new EventEmitter<string>();
  @Output() monthChange = new EventEmitter<{ month: number; year: number }>();

  @HostBinding('class.munily-df-host--ds-default')
  get hostDsDefaultMaxWidth(): boolean {
    return this.widthLayout === 'ds' && !this.maxWidth;
  }

  @HostBinding('style.max-width')
  get hostMaxWidthStyle(): string | null {
    if (this.widthLayout === 'fill') {
      return 'none';
    }
    if (this.widthLayout === 'ds' && this.maxWidth) {
      return this.maxWidth;
    }
    return null;
  }

  readonly fallbackControl = new FormControl('');
  private readonly _destroy$ = new Subject<void>();
  private _pickerDialogRef: MatDialogRef<MunilyDatePickerModalComponent> | null = null;

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly _dialog: MatDialog,
  ) {}

  get resolvedControl(): FormControl {
    return this.control ?? this.fallbackControl;
  }

  get materialAppearance(): 'outline' | 'fill' {
    return this.appearance === 'filled' ? 'fill' : 'outline';
  }

  get displayValue(): string {
    const raw = this.resolvedControl.value;
    if (raw == null || String(raw).trim() === '') {
      return '';
    }
    return this._formatIsoForDisplay(String(raw).trim());
  }

  get resolvedErrorMessage(): string {
    if (this.errorMessage) return this.errorMessage;
    const errors = this.resolvedControl.errors;
    if (!errors) return '';
    return this._firstErrorMessage(errors);
  }

  ngOnInit(): void {
    this._syncDisabled();
    this._wireControlUpdates();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this._syncDisabled();
    }
    if (changes['control']) {
      this._destroy$.next();
      this._syncDisabled();
      this._wireControlUpdates();
    }
    if (changes['disabledDates'] && this._pickerDialogRef?.componentInstance) {
      this._pickerDialogRef.componentInstance.refreshDateFilter();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  showError(): boolean {
    const c = this.resolvedControl;
    return c.invalid && (c.touched || c.dirty);
  }

  showSuccessOutline(): boolean {
    return this.successful && !this.showError();
  }

  openPicker(): void {
    if (this.disabled || this.resolvedControl.disabled) return;

    const raw = this.resolvedControl.value;
    const data: MunilyDatePickerModalData = {
      titleKey: this.pickerTitleKey,
      initialDate: raw ? String(raw).trim() : undefined,
      minDate: this.minDate,
      maxDate: this.maxDate,
      isDateDisabled: (isoDate) => this.disabledDates.includes(isoDate),
      onMonthChange: (event) => this.monthChange.emit(event),
    };

    this._pickerDialogRef = this._dialog.open(MunilyDatePickerModalComponent, {
      panelClass: MUNILY_DATE_PICKER_MODAL_PANEL_CLASS,
      width: '376px',
      maxWidth: '96vw',
      autoFocus: false,
      restoreFocus: true,
      data,
    });

    this._pickerDialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        this._pickerDialogRef = null;
        if (!result?.date) return;
        this.resolvedControl.setValue(result.date);
        this.resolvedControl.markAsDirty();
        this.resolvedControl.markAsTouched();
        this.valueChange.emit(result.date);
        this._cdr.markForCheck();
      });
  }

  onInputClick(): void {
    this.openPicker();
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openPicker();
    }
  }

  onBlur(): void {
    this.resolvedControl.markAsTouched();
  }

  private _formatIsoForDisplay(iso: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) return iso;
    const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  }

  private _syncDisabled(): void {
    const ctrl = this.resolvedControl;
    if (this.disabled && ctrl.enabled) {
      ctrl.disable({ emitEvent: false });
    } else if (!this.disabled && ctrl.disabled) {
      ctrl.enable({ emitEvent: false });
    }
  }

  private _wireControlUpdates(): void {
    const ctrl = this.resolvedControl;
    ctrl.statusChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this._cdr.markForCheck());
    ctrl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this._cdr.markForCheck());
  }

  private _firstErrorMessage(errors: ValidationErrors): string {
    const key = Object.keys(errors)[0];
    const custom = this.errorMessages[key];
    if (custom) return custom;

    const err = errors[key];
    if (key === 'required') return 'Este campo es obligatorio';
    if (typeof err === 'object' && err?.message) return String(err.message);
    return 'Valor no válido';
  }
}
